/**
 * Worker rendering — one InstancedMesh for all workers (cap 160), now with
 * skeletal animation.
 *
 * Each worker gets a per-instance animation state (one AnimController per
 * worker — 160 tiny state machines, not per-vertex CPU work). The sim state
 * (WorkerState + node/farm/site) maps to an AnimActivity in
 * render/anim.ts; the vertex shader (render/skinned.ts) samples the baked
 * bone atlas, so per-frame CPU cost is a few floats per worker.
 *
 * Tool props (hammer, saw) are separate InstancedMeshes bound 100% to the
 * hand bones: they ride the faber work clips with zero extra CPU work and
 * are only drawn for workers in the matching activity.
 *
 * Carrying workers keep the lighter tint so drop-off runs stay visible.
 */
import * as THREE from "three";
import type { Worker } from "../sim/workers";
import {
  AnimController,
  AnimSystem,
  FRAMES_PER_CLIP,
  BONE_COUNT,
  boneIndex,
  workerActivity,
  type AnimActivity,
} from "./anim";
import {
  attachAnimAttributes,
  buildHumanoidGeometry,
  buildPropGeometry,
  flagAnimAttributes,
  makeSkinnedMaterial,
  writeAnimPose,
  type AnimAttributes,
} from "./skinned";
import { buildFaberWorkerGeometry } from "./faber_geo";
import type { ResolvedModel } from "../data/artmap";
import type { ModelStore } from "./models";

const CAP = 160;
const tmp = new THREE.Object3D();
const tmpColor = new THREE.Color();

/** Activities that show the hammer prop / saw prop. */
const HAMMER_ACTIVITIES: ReadonlySet<AnimActivity> = new Set([
  "hammer_high",
  "hammer_low",
  "repair",
]);
const SAW_ACTIVITIES: ReadonlySet<AnimActivity> = new Set(["saw"]);

export class WorkerField {
  readonly mesh: THREE.InstancedMesh;
  /** instanceId -> worker for click selection. */
  picked: Worker[] = [];

  private attrs: AnimAttributes;
  private controllers: AnimController[] = [];
  private sys: AnimSystem;
  private hammer: THREE.InstancedMesh;
  private saw: THREE.InstancedMesh;
  private hammerAttrs: AnimAttributes;
  private sawAttrs: AnimAttributes;

  constructor(
    scene: THREE.Scene,
    sys: AnimSystem,
  ) {
    this.sys = sys;
    const geo = buildHumanoidGeometry();
    // White vertex colours: the material uses vertexColours so the real
    // faber geometry (loaded async below) can carry its own part colours;
    // the procedural fallback stays untextured white x team tint.
    const count = geo.getAttribute("position").count;
    const white = new Float32Array(count * 3).fill(1);
    geo.setAttribute("color", new THREE.BufferAttribute(white, 3));
    this.attrs = attachAnimAttributes(geo, CAP);
    const mat = makeSkinnedMaterial(sys.texture, BONE_COUNT, FRAMES_PER_CLIP);
    mat.vertexColors = true;
    this.mesh = new THREE.InstancedMesh(geo, mat, CAP);
    this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = true;
    scene.add(this.mesh);
    for (let i = 0; i < CAP; i++) {
      // Workers use the faber clip set: their sim states are the faber
      // behavior vocabulary (construct/saw/dig/repair).
      this.controllers.push(new AnimController(sys, true));
    }

    const propMat = makeSkinnedMaterial(
      sys.texture,
      BONE_COUNT,
      FRAMES_PER_CLIP,
    );
    const hammerGeo = buildPropGeometry("hammer", boneIndex("handR"));
    this.hammerAttrs = attachAnimAttributes(hammerGeo, CAP);
    this.hammer = new THREE.InstancedMesh(hammerGeo, propMat, CAP);
    this.hammer.count = 0;
    this.hammer.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.hammer.frustumCulled = false;
    scene.add(this.hammer);

    const sawGeo = buildPropGeometry("saw", boneIndex("handL"));
    this.sawAttrs = attachAnimAttributes(sawGeo, CAP);
    this.saw = new THREE.InstancedMesh(sawGeo, propMat, CAP);
    this.saw.count = 0;
    this.saw.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.saw.frustumCulled = false;
    scene.add(this.saw);
  }

  /**
   * REAL-GEOMETRY SWAP: load the vendored faber GLB, rebind it onto the
   * placeholder rig (faber_geo.ts), and replace the procedural humanoid
   * with the real model. Async, never rejects, never blocks the sim: the
   * placeholder renders until the swap lands. Tool meshes stay excluded —
   * the instanced hammer/saw props keep work-state readability.
   */
  attachRealGeometry(model: ResolvedModel, store: ModelStore): void {
    if (!model.available) return;
    void store
      .load(model)
      .then((group) => {
        if (!group) return;
        const geo = buildFaberWorkerGeometry(group);
        if (!geo) return;
        const old = this.mesh.geometry;
        this.mesh.geometry = geo;
        old.dispose();
        console.info("[workers] real faber geometry attached");
      })
      .catch(() => {
        // Keep the procedural humanoid.
      });
  }

  sync(
    workers: Worker[],
    groundY: (x: number, z: number) => number,
    playerTint: number,
    enemyTint: number,
    simNow: number,
    dt: number,
  ): void {
    this.picked = workers.slice(0, CAP);
    this.mesh.count = this.picked.length;
    // three.js caches InstancedMesh.boundingSphere: a sphere computed once
    // (e.g. while count was 0 behind the faction-select screen, or from
    // spawn positions before workers walked to their nodes) stays stale
    // forever and makes selection raycasts miss. Workers move every frame,
    // so invalidate unconditionally — the next raycast recomputes from
    // live matrices (160 instances: negligible, click-time only).
    this.mesh.boundingSphere = null;
    let hammers = 0;
    let saws = 0;
    for (let i = 0; i < this.picked.length; i++) {
      const w = this.picked[i];
      tmp.position.set(w.x, groundY(w.x, w.z), w.z);
      tmp.rotation.set(0, 0, 0);
      tmp.updateMatrix();
      this.mesh.setMatrixAt(i, tmp.matrix);
      const base = w.team === 0 ? playerTint : enemyTint;
      tmpColor.set(base);
      if (w.carried > 0) tmpColor.lerp(new THREE.Color(0xffffff), 0.45);
      this.mesh.setColorAt(i, tmpColor);

      const activity = workerActivity(
        {
          state: w.state,
          nodeKind: w.node ? w.node.kind : null,
          hasFarm: w.farm !== null,
        },
        // Per-worker phase so hammer strikes don't synchronize.
        simNow + w.id * 0.37,
      );
      const pose = this.controllers[i].update(
        Math.min(dt, 0.1),
        activity,
      );
      const durA = this.sys.durationOf(pose.a);
      const durB = this.sys.durationOf(pose.b);
      const frameA = (pose.timeA / durA) * (FRAMES_PER_CLIP - 1);
      const frameB = (pose.timeB / durB) * (FRAMES_PER_CLIP - 1);
      writeAnimPose(
        this.attrs,
        i,
        pose.a,
        frameA,
        pose.b,
        frameB,
        pose.blend,
      );

      // Props follow the same pose; compact them into their own meshes.
      if (HAMMER_ACTIVITIES.has(activity)) {
        this.hammer.setMatrixAt(hammers, tmp.matrix);
        writeAnimPose(
          this.hammerAttrs,
          hammers,
          pose.a,
          frameA,
          pose.b,
          frameB,
          pose.blend,
        );
        hammers++;
      } else if (SAW_ACTIVITIES.has(activity)) {
        this.saw.setMatrixAt(saws, tmp.matrix);
        writeAnimPose(
          this.sawAttrs,
          saws,
          pose.a,
          frameA,
          pose.b,
          frameB,
          pose.blend,
        );
        saws++;
      }
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    flagAnimAttributes(this.attrs);
    this.hammer.count = hammers;
    this.saw.count = saws;
    this.hammer.instanceMatrix.needsUpdate = true;
    this.saw.instanceMatrix.needsUpdate = true;
    flagAnimAttributes(this.hammerAttrs);
    flagAnimAttributes(this.sawAttrs);
  }
}
